import axios from "axios";

const API_URL =
  import.meta.env.VITE_APP_API_URL || "https:/@/shared/services/api.pidepasta.com/v1";
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
let refreshTokenRequest: Promise<string | null> | null = null;

const getTokenExpiration = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("modules");
  localStorage.removeItem("name");

  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const refreshAccessToken = async (token: string): Promise<string | null> => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = axios
      .post(
        `${API_URL}/admin/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((response) => {
        const newToken = response.data?.token || response.data?.data?.token;
        if (!newToken) return token;
        localStorage.setItem("token", newToken);
        return newToken;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }

  return refreshTokenRequest;
};

// Crear instancia de Axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para incluir el token en las cabeceras de cada solicitud
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("token");
    let accessToken = token;

    if (token && !String(config.url || "").includes("/admin/refresh-token")) {
      const expiresAt = getTokenExpiration(token);
      if (expiresAt && expiresAt - Date.now() <= TOKEN_REFRESH_THRESHOLD_MS) {
        try {
          accessToken = await refreshAccessToken(token);
        } catch {
          clearSessionAndRedirect();
          return Promise.reject(new Error("No se pudo refrescar la sesión"));
        }
      }
    }

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    if (config.data instanceof FormData && config.headers) {
      const headers = config.headers as any;

      if (typeof headers.delete === "function") {
        headers.delete("Content-Type");
      } else {
        delete headers["Content-Type"];
        delete headers["content-type"];
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor de respuesta para manejar sesiones inválidas o expiradas.
api.interceptors.response.use(
  (response) => response, // Si la respuesta es exitosa, la retorna tal cual
  (error) => {
    if (error.response?.status === 401) {
      clearSessionAndRedirect();
    }

    // Si no es 401 o el refresh falla, rechaza la promesa con el error original
    return Promise.reject(error);
  },
);

export default api;
